import { escapeHtml } from "../chrome";
import { isAllowedEmail } from "./email";

/**
 * Multi-select for choosing who can open an internal app.
 *
 * Replaces a free-text email field. Typing an address by hand meant a typo
 * silently locked the wrong person out — and locked the intended person out
 * too, with no feedback until they clicked the link and got a 403.
 *
 * Groups are offered alongside individuals and are stored as group names, not
 * expanded to emails. The API resolves them at access time, so a joiner gets
 * into apps already shared with their group without anyone re-uploading.
 */

export interface Directory {
  /** Realm group names shareable with. Membership is asserted by Keycloak
   * at sign-in, so we deliberately do not know or show the member list. */
  groups: string[];
  people: string[];
  /** Domains a hand-typed address must end in. Supplied by the API. */
  domains: string[];
}

export interface Selection {
  emails: string[];
  groups: string[];
}

interface Entry {
  kind: "group" | "person";
  value: string;
  label: string;
  sub: string;
}

/** "lara.smith@twentyfirstgroup.com" → "Lara Smith" */
function displayName(email: string): string {
  const local = email.split("@")[0] ?? email;
  return local
    .split(/[.\-_+]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function mountPeoplePicker(
  root: HTMLElement,
  directory: Directory,
  onChange: (sel: Selection) => void,
  initial: Selection = { emails: [], groups: [] },
): void {
  const entries: Entry[] = [
    ...directory.groups.map((name) => ({
      kind: "group" as const,
      value: name,
      label: name,
      // No member count: membership comes from the user's Keycloak token at
      // sign-in, driven by their Google group, and this app never sees the
      // roster. Showing "0 people" would be worse than saying nothing.
      sub: "Group · membership from Keycloak",
    })),
    ...directory.people.map((email) => ({
      kind: "person" as const,
      value: email,
      label: displayName(email),
      sub: email,
    })),
  ];

  const chosen = new Set<string>();
  // Prefill when editing an existing app. An address that has since dropped
  // out of the directory still needs showing — it is on the list, and hiding
  // it would silently drop someone's access on the next save.
  for (const g of initial.groups) chosen.add(`group:${g}`);
  for (const e of initial.emails) {
    const email = e.trim().toLowerCase();
    if (!entries.some((x) => x.kind === "person" && x.value === email)) {
      entries.push({ kind: "person", value: email, label: displayName(email), sub: email });
    }
    chosen.add(`person:${email}`);
  }
  const key = (e: Entry): string => `${e.kind}:${e.value}`;
  let query = "";

  root.innerHTML = `
    <div class="rounded border border-ink-border bg-ink-surface">
      <div id="pp-chips" class="flex flex-wrap gap-1.5 px-2 pt-2"></div>
      <input id="pp-search" class="w-full bg-transparent px-3 py-2 text-sm outline-none placeholder:text-bone-muted"
             placeholder="Search people and groups, or type an email address…" autocomplete="off" />
      <div id="pp-list" class="max-h-56 overflow-y-auto border-t border-ink-border"></div>
    </div>
  `;

  const chips = root.querySelector<HTMLElement>("#pp-chips")!;
  const search = root.querySelector<HTMLInputElement>("#pp-search")!;
  const list = root.querySelector<HTMLElement>("#pp-list")!;

  function emit(): void {
    const sel: Selection = { emails: [], groups: [] };
    for (const k of chosen) {
      const [kind, ...rest] = k.split(":");
      const value = rest.join(":");
      if (kind === "group") sel.groups.push(value);
      else sel.emails.push(value);
    }
    onChange(sel);
  }

  function renderChips(): void {
    if (chosen.size === 0) {
      chips.innerHTML = "";
      return;
    }
    chips.innerHTML = [...chosen]
      .map((k) => {
        const e = entries.find((x) => key(x) === k);
        const label = e ? e.label : k;
        const isGroup = k.startsWith("group:");
        return `
          <span class="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${
            isGroup ? "bg-tfg/15 text-tfg" : "bg-ink text-bone-muted"
          }">
            ${escapeHtml(label)}
            <button data-remove="${escapeHtml(k)}" class="hover:text-bone" aria-label="Remove ${escapeHtml(label)}">×</button>
          </span>`;
      })
      .join("");
    chips.querySelectorAll<HTMLButtonElement>("[data-remove]").forEach((b) => {
      b.addEventListener("click", () => {
        chosen.delete(b.dataset.remove!);
        renderChips();
        renderList();
        emit();
      });
    });
  }

  function renderList(): void {
    const q = query.trim().toLowerCase();
    const matches = entries.filter(
      (e) => !q || e.label.toLowerCase().includes(q) || e.sub.toLowerCase().includes(q),
    );

    // Offer to add a hand-typed address when it is not already in the
    // directory. This is how a new joiner gets access without a deploy.
    const typed = query.trim().toLowerCase();
    const known = entries.some((e) => e.kind === "person" && e.value === typed);
    const canAdd = typed.length > 0 && !known && isAllowedEmail(typed, directory.domains);
    const looksLikeEmail = typed.includes("@");

    const addRow = canAdd
      ? `<button type="button" data-add="${escapeHtml(typed)}"
                 class="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-ink/60 border-b border-ink-border">
           <span>
             <span class="text-sm text-bone">Add ${escapeHtml(typed)}</span>
             <span class="text-xs text-bone-muted ml-2">not in the directory</span>
           </span>
           <span class="text-xs text-tfg">+</span>
         </button>`
      : "";

    // A typed address on the wrong domain is a dead end, so say why rather
    // than showing "no match" — they cannot sign in at all.
    const rejected =
      looksLikeEmail && !canAdd && !known && typed.includes(".")
        ? `<p class="px-3 py-2 text-xs text-gold">
             Only ${escapeHtml(directory.domains.join(", "))} addresses can be given access —
             anyone else can't sign in.
           </p>`
        : "";

    if (matches.length === 0 && !addRow) {
      list.innerHTML =
        rejected ||
        `<p class="px-3 py-3 text-xs text-bone-muted">No match. Everyone at TFG can open this app unless you pick someone.</p>`;
      return;
    }

    list.innerHTML =
      addRow +
      rejected +
      matches
        .map((e) => {
          const on = chosen.has(key(e));
          return `
          <button type="button" data-key="${escapeHtml(key(e))}"
                  class="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-ink/60 ${
                    on ? "bg-ink/40" : ""
                  }">
            <span>
              <span class="text-sm ${on ? "text-bone" : "text-bone-muted"}">${escapeHtml(e.label)}</span>
              <span class="text-xs text-bone-muted ml-2">${escapeHtml(e.sub)}</span>
            </span>
            <span class="text-xs ${on ? "text-tfg" : "text-ink-muted"}">${on ? "✓" : "+"}</span>
          </button>`;
        })
        .join("");

    list.querySelectorAll<HTMLButtonElement>("[data-add]").forEach((b) => {
      b.addEventListener("click", () => addCustom(b.dataset.add!));
    });

    list.querySelectorAll<HTMLButtonElement>("[data-key]").forEach((b) => {
      b.addEventListener("click", () => {
        const k = b.dataset.key!;
        if (chosen.has(k)) chosen.delete(k);
        else chosen.add(k);
        renderChips();
        renderList();
        emit();
      });
    });
  }

  function addCustom(email: string): void {
    if (!isAllowedEmail(email, directory.domains)) return;
    const e: Entry = {
      kind: "person",
      value: email.trim().toLowerCase(),
      label: displayName(email),
      sub: email.trim().toLowerCase(),
    };
    if (!entries.some((x) => key(x) === key(e))) entries.push(e);
    chosen.add(key(e));
    query = "";
    search.value = "";
    renderChips();
    renderList();
    emit();
  }

  search.addEventListener("input", () => {
    query = search.value;
    renderList();
  });

  // Enter adds a typed address without reaching for the mouse.
  search.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter") return;
    ev.preventDefault();
    const typed = search.value.trim().toLowerCase();
    if (isAllowedEmail(typed, directory.domains)) addCustom(typed);
  });

  renderChips();
  renderList();
}
